import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './components/AdminLayout';
import { API_ENDPOINTS } from '../config/api';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching customers from:', API_ENDPOINTS.ADMIN.CUSTOMERS);
      
      const response = await axios.get(API_ENDPOINTS.ADMIN.CUSTOMERS, {
        timeout: 8000,
      });
      
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      handleApiError(error, 'fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleApiError = (error, operation) => {
    if (error.code === 'ECONNABORTED') {
      setError(`Request timeout while trying to ${operation}. Please check your connection.`);
    } else if (error.response) {
      setError(`Server error: ${error.response.status}. Failed to ${operation}.`);
    } else if (error.request) {
      setError(`Network error. Please check if the server is running at ${API_ENDPOINTS.ADMIN.CUSTOMERS}`);
    } else {
      setError(`Unable to ${operation}. Please try again later.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await axios.post(API_ENDPOINTS.ADMIN.CUSTOMERS, formData, {
        timeout: 8000,
      });
      
      await fetchCustomers();
      setShowForm(false);
      setFormData({ name: '', phone: '', email: '', address: '' });
      alert('Customer added successfully!');
    } catch (error) {
      console.error('Error creating customer:', error);
      handleApiError(error, 'add customer');
      alert('Error adding customer. Please check the error message below.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        setError('');
        await axios.delete(`${API_ENDPOINTS.ADMIN.CUSTOMERS}/${id}`, {
          timeout: 8000,
        });
        
        await fetchCustomers();
        alert('Customer deleted successfully!');
      } catch (error) {
        console.error('Error deleting customer:', error);
        
        if (error.response && error.response.status === 400) {
          alert('Cannot delete customer with existing orders. Please delete their orders first.');
        } else {
          handleApiError(error, 'delete customer');
          alert('Error deleting customer. Please check the error message below.');
        }
      }
    }
  };

  return (
    <AdminLayout>
      <div className="admin-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Customer Management</h2>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            <i className="fas fa-plus me-2"></i>Add Customer
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {showForm && (
          <div className="card form-container mb-4">
            <div className="card-header">
              <h4 className="mb-0">Add New Customer</h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Phone</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Address</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    disabled={loading}
                  ></textarea>
                </div>
                <div className="d-flex gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      'Save Customer'
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Customer List</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading customers...</p>
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-users fa-3x text-muted mb-3"></i>
                <p className="text-muted">No customers found.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowForm(true)}
                >
                  Add First Customer
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Address</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map(customer => (
                      <tr key={customer.id}>
                        <td>{customer.id}</td>
                        <td>
                          <strong>{customer.name}</strong>
                          {customer.created_date && (
                            <div className="text-muted small">
                              Added: {new Date(customer.created_date).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td>{customer.phone || '-'}</td>
                        <td>{customer.email || '-'}</td>
                        <td>
                          {customer.address ? (
                            <span title={customer.address}>
                              {customer.address.length > 30 
                                ? `${customer.address.substring(0, 30)}...` 
                                : customer.address
                              }
                            </span>
                          ) : '-'}
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(customer.id)}
                            disabled={loading}
                            title="Delete Customer"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CustomerManagement;